import { useEffect, useState } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import FloatingInput from "./floatingInput";
import { isPasswordValid, passwordRequirements } from "@/lib/utils";

type Props = {
	label: string;
	value: string;
	onChange: (value: string) => void;
	error?: { message?: string };
	onFocus?: () => void;
	onBlur?: () => void;
	onValidationChange?: (valid: boolean) => void;
};

export default function PasswordWithRequirements({
	label,
	value,
	onChange,
	error,
	onFocus,
	onBlur,
	onValidationChange,
}: Props) {
	const [showRequirements, setShowRequirements] = useState(false);

	const requirements = passwordRequirements.map((req) => ({
		id: req.id,
		label: req.label,
		valid: req.isValid(value),
	}));

	const isValid = isPasswordValid(value);

	useEffect(() => {
		onValidationChange?.(isValid);
	}, [isValid]); // If onValidationChange is added to the dependency array, then the parent components need to generate the call back with useCallback

	return (
		<div>
			<FloatingInput
				type="password"
				label={label}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				onFocus={() => {
					setShowRequirements(true);
					onFocus?.();
				}}
				onBlur={() => {
					if (isPasswordValid(value)) {
						setShowRequirements(false);
					}
					onBlur?.();
				}}
				error={error ? { type: "manual", ...error } : undefined}
			/>

			{showRequirements && (
				<div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-md">
					<p className="text-sm font-medium text-textDark mb-2">
						Password requirements:
					</p>
					<ul className="space-y-1 text-sm">
						{requirements.map((req) => {
							const color = req.valid ? "text-green-600" : "text-gray-400";
							const Icon = req.valid ? CheckCircle : XCircle;
							return (
								<li key={req.id} className={`flex items-center ${color}`}>
									<Icon className="w-5 h-5 mr-2" />
									{req.label}
								</li>
							);
						})}
					</ul>
				</div>
			)}
		</div>
	);
}
